import { expandToLonghand } from "@/lib/electronConfig";
import { nobleGasConfig } from "@/data/elementDetails";

export interface SubshellOccupancy {
  n: number;
  l: number; // 0=s, 1=p, 2=d, 3=f
  label: string; // "1s", "2p", ...
  electrons: number;
}

export type ConfigOrder = "subshell" | "energy";

const DIGIT_TO_SUPER: Record<string, string> = {
  "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴",
  "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹",
};

function toSuperscript(n: number): string {
  return String(n)
    .split("")
    .map((c) => DIGIT_TO_SUPER[c] ?? c)
    .join("");
}

const L_LETTERS = ["s", "p", "d", "f"];

/** Sort subshells by chosen ordering.
 *  - "subshell": by n, then by l (1s, 2s, 2p, 3s, 3p, 3d, 4s, 4p, ...)
 *  - "energy":  Madelung / Aufbau (n+l asc, then n asc) - gives 1s, 2s, 2p, 3s, 3p, 4s, 3d, 4p, 5s, 4d, ... */
export function sortSubshells(
  subs: SubshellOccupancy[],
  order: ConfigOrder,
): SubshellOccupancy[] {
  const copy = [...subs];
  if (order === "energy") {
    copy.sort((a, b) => {
      const sumA = a.n + a.l;
      const sumB = b.n + b.l;
      if (sumA !== sumB) return sumA - sumB;
      return a.n - b.n;
    });
  } else {
    copy.sort((a, b) => (a.n !== b.n ? a.n - b.n : a.l - b.l));
  }
  return copy;
}

/** Render an ordered list of subshells back into a config string. */
export function formatConfig(subs: SubshellOccupancy[]): string {
  return subs
    .map((s) => `${s.n}${L_LETTERS[s.l]}${toSuperscript(s.electrons)}`)
    .join(" ");
}

const SUPER_TO_DIGIT: Record<string, string> = {
  "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4",
  "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9",
};

const L_FROM_LETTER: Record<string, number> = { s: 0, p: 1, d: 2, f: 3 };

/** Parse a longhand config like "1s² 2s² 2p⁶ 3s¹" into subshell occupancies. */
export function parseLonghand(longhand: string): SubshellOccupancy[] {
  const tokens = longhand.split(/\s+/).filter(Boolean);
  const out: SubshellOccupancy[] = [];
  for (const tok of tokens) {
    const m = tok.match(/^(\d+)([spdf])(.+)$/);
    if (!m) continue;
    const n = parseInt(m[1], 10);
    const l = L_FROM_LETTER[m[2]];
    const digits = m[3]
      .split("")
      .map((c) => SUPER_TO_DIGIT[c] ?? c)
      .join("");
    const electrons = parseInt(digits, 10);
    if (Number.isNaN(electrons)) continue;
    out.push({ n, l, label: `${n}${m[2]}`, electrons });
  }
  return out;
}

/** Distribute N electrons into (2l+1) orbital boxes following Hund's rule.
 *  Returns an array of orbital arrow counts (0, 1=single up, 2=paired). */
export function fillOrbitals(l: number, electrons: number): number[] {
  const slots = 2 * l + 1;
  const boxes = new Array(slots).fill(0);
  let remaining = electrons;
  // Hund: one electron in each first
  for (let i = 0; i < slots && remaining > 0; i++) {
    boxes[i] = 1;
    remaining--;
  }
  // Then pair up
  for (let i = 0; i < slots && remaining > 0; i++) {
    boxes[i] = 2;
    remaining--;
  }
  return boxes;
}

export function getOrbitalDiagram(elementNumber: number, fallback: string) {
  const config =
    nobleGasConfig[elementNumber]
      ? expandToLonghand(nobleGasConfig[elementNumber], nobleGasConfig)
      : fallback;
  return parseLonghand(config);
}
